// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: About ZenFresh  (route /about-app — the app-style "About ZenFresh" screen, linked from the profile page)
// A compact mobile version of the story, using only what the site really says: the mission (data/companyProfile), the four core values
// (data/coreValues — title + one line), the cities served (data/locations), the registrations that exist (data/legalRegistrations —
// today none, and the page says so), the real policy links, and the company name (data/legalContact). Share uses the device share sheet
// or copies the link. The long-form story stays on /about.
// Deliberately NOT here (no data/system behind it): an app version / "clean cold-chain build" chip, "3 AM harbour haul", "0–4 °C
// chilled chain", "100 % traceable" stats, "hook-caught by artisanal day-boat crews / iced at 5 AM / before breakfast", "ozonated
// sanitised ice water", "fair-trade payouts / juvenile-free / 100 % recyclable / plastic-neutral", an FSSAI licence number, "NABL lab
// assayed / zero chemical bath", the "Cold-Chain Logistics Award 2024", social-media handles (the site's links are placeholders), a
// "Pvt. Ltd." company name, "© 2024", and harbour hubs (Mumbai • Kochi • Mangalore • Goa) that the site doesn't list as hubs.
// Neighbours: pages/About (full story), pages/LegalRegistrations, pages/Compliance, pages/Policies, pages/Faq.
// ─────────────────────────────────────────────────────────────────────────────
import { Link } from "react-router-dom";
import { ArrowLeft, Check, FileText, HelpCircle, Lock, MapPin, Share2 } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import { MISSION, VISION } from "../data/companyProfile";
import { CORE_VALUES } from "../data/coreValues";
import { COMPANY_NAME } from "../data/legalContact";
import { COMPANY_REGISTRATION, FSSAI_LICENCES, GSTIN_RECORD } from "../data/legalRegistrations";
import { CITIES } from "../data/locations";
import useCopyToClipboard from "../hooks/useCopyToClipboard";
import useDocumentTitle from "../hooks/useDocumentTitle";

const card = "rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10";

export default function AboutApp() {
  useDocumentTitle("About ZenFresh");
  const { copy, copied } = useCopyToClipboard();
  const hasRegistrations = GSTIN_RECORD !== null || COMPANY_REGISTRATION !== null || FSSAI_LICENCES.length > 0;

  const share = async () => {
    const data = { title: "ZenFresh", text: MISSION, url: window.location.href };
    if (typeof navigator.share === "function") {
      try {
        await navigator.share(data);
      } catch {
        // The customer dismissed the share sheet — nothing to do.
      }
    } else {
      void copy(window.location.href);
    }
  };

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-24 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs />
        <div className="mx-auto flex max-w-xl flex-col gap-4 px-4 pt-2">
          <header className="flex items-center gap-3">
            <Link to="/profile" aria-label="Back to my profile" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10">
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <h1 className="min-w-0 flex-1 font-display text-2xl font-bold text-ocean-950 dark:text-foam-50">About ZenFresh</h1>
            <button type="button" onClick={() => void share()} aria-label="Share ZenFresh" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10">
              {copied ? <Check className="h-5 w-5 text-teal-700" aria-hidden="true" /> : <Share2 className="h-5 w-5" aria-hidden="true" />}
            </button>
          </header>
          <p role="status" className="sr-only">{copied ? "Link copied." : ""}</p>

          <section className="flex flex-col items-center rounded-2xl bg-gradient-to-b from-ocean-700/10 to-white p-6 text-center shadow-sm dark:from-white/5 dark:to-ocean-900">
            <img src="/logo.png" alt="" width={80} height={80} className="h-20 w-20 rounded-full bg-white object-contain p-2 shadow-md" />
            <h2 className="mt-3 font-display text-2xl font-bold text-ocean-950 dark:text-foam-50">ZenFresh</h2>
            <p className="text-sm font-semibold text-teal-700 dark:text-teal-300">Fresh coastal seafood, delivered</p>
            <blockquote className="mt-4 rounded-xl bg-white/70 p-4 text-sm italic leading-relaxed text-ocean-800/90 dark:bg-white/5 dark:text-foam-100/90">&ldquo;{MISSION}&rdquo;</blockquote>
            <p className="mt-3 text-xs text-ocean-800/80 dark:text-foam-100/75">{VISION}</p>
          </section>

          <Link to="/coming-soon/our-standard" className="flex min-h-12 items-center justify-between gap-3 rounded-2xl bg-coral-500/10 px-4 py-3 text-sm font-bold text-ocean-950 ring-1 ring-coral-500/30 dark:text-foam-50">
            <span>The ZenFresh standard</span>
            <span className="shrink-0 rounded-full bg-coral-500/20 px-2 py-0.5 text-[10px] text-coral-600 dark:text-coral-300">Coming soon</span>
          </Link>
          <section aria-labelledby="values-h" className="flex flex-col gap-2">
            <h2 id="values-h" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">What we stand for</h2>
            <ul className="flex flex-col gap-2">
              {CORE_VALUES.map(({ icon: Icon, title, body }) => (
                <li key={title} className={`${card} flex items-start gap-3`}>
                  <span aria-hidden="true" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ocean-700/10 text-ocean-800 dark:bg-white/10 dark:text-foam-100"><Icon className="h-6 w-6" /></span>
                  <div>
                    <h3 className="text-sm font-bold text-ocean-950 dark:text-foam-50">{title}</h3>
                    <p className="mt-0.5 text-xs text-ocean-800/90 dark:text-foam-100/85">{body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section aria-labelledby="where-h" className={card}>
            <h2 id="where-h" className="flex items-center gap-2 font-display text-lg font-bold text-ocean-950 dark:text-foam-50"><MapPin className="h-5 w-5" aria-hidden="true" /> Where we deliver</h2>
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {CITIES.map((city) => <li key={city.name} className="rounded-full bg-ocean-700/10 px-2.5 py-1 text-xs font-semibold text-ocean-900 dark:bg-white/10 dark:text-foam-50">{city.name}</li>)}
            </ul>
            <Link to="/locations" className="mt-3 inline-flex min-h-11 items-center text-sm font-bold underline">Check a pincode</Link>
          </section>

          <section aria-labelledby="trust-h" className={card}>
            <h2 id="trust-h" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Safety &amp; registrations</h2>
            <p className="mt-1 text-sm text-ocean-800/90 dark:text-foam-100/85">
              {hasRegistrations
                ? "Our registrations are listed on the Legal & registrations page."
                : "Our food-safety information is on the Compliance page. Registration numbers (GSTIN, CIN, FSSAI) are shown on the Legal & registrations page only once they have been issued to us — none are listed yet."}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link to="/compliance" className="inline-flex min-h-11 items-center rounded-full bg-ocean-700/10 px-4 text-sm font-bold text-ocean-900 dark:bg-white/10 dark:text-foam-50">Compliance</Link>
              <Link to="/legal" className="inline-flex min-h-11 items-center rounded-full bg-ocean-700/10 px-4 text-sm font-bold text-ocean-900 dark:bg-white/10 dark:text-foam-50">Legal &amp; registrations</Link>
            </div>
          </section>

          <nav aria-label="Policies and help" className={`${card} p-2`}>
            <ul className="divide-y divide-ocean-900/10 dark:divide-white/10">
              {[
                { to: "/terms", icon: FileText, label: "Terms & Conditions" },
                { to: "/privacy", icon: Lock, label: "Privacy Policy" },
                { to: "/faq", icon: HelpCircle, label: "Questions & answers" },
              ].map(({ to, icon: Icon, label }) => (
                <li key={to}>
                  <Link to={to} className="flex min-h-12 items-center gap-3 px-3 text-sm font-bold text-ocean-900 dark:text-foam-50"><Icon className="h-5 w-5 text-ocean-800 dark:text-foam-100" aria-hidden="true" /> {label}</Link>
                </li>
              ))}
            </ul>
          </nav>

          <Link to="/about" className="inline-flex min-h-12 items-center justify-center rounded-full bg-ocean-800 px-6 font-bold text-white shadow-md hover:bg-ocean-700">Read our full story</Link>
          <p className="text-center text-xs text-ocean-800/70 dark:text-foam-100/65">ZenFresh is operated by {COMPANY_NAME}.</p>
        </div>
      </div>
    </PageTransition>
  );
}
